import type { IAuthProvider } from './types'

let instance: IAuthProvider | null = null

/**
 * Returns a singleton IAuthProvider based on VITE_AUTH_PROVIDER.
 * Throws if the provider value is unrecognised.
 *
 * Providers are loaded via dynamic import so that the Cognito SDK
 * (which references the Node.js global `global`) is never bundled
 * or executed in environments that only use Keycloak, and vice-versa.
 *
 * The singleton is module-scoped. In tests, call vi.resetModules() between
 * test cases to get a fresh module (and a fresh singleton).
 */
export async function createAuthProvider(): Promise<IAuthProvider> {
  if (instance !== null) {
    return instance
  }

  const provider = import.meta.env.VITE_AUTH_PROVIDER

  if (provider === 'keycloak') {
    const { KeycloakAuthProvider } = await import('./KeycloakAuthProvider')
    instance = new KeycloakAuthProvider({
      url: import.meta.env.VITE_KEYCLOAK_URL!,
      realm: import.meta.env.VITE_KEYCLOAK_REALM!,
      clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID!,
    })
    return instance
  }

  if (provider === 'cognito') {
    // validateAuthConfig() runs before createAuthProvider() is called,
    // so the non-null assertions below are safe.
    const { CognitoAuthProvider } = await import('./CognitoAuthProvider')
    instance = new CognitoAuthProvider({
      region: import.meta.env.VITE_COGNITO_REGION!,
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID!,
      clientId: import.meta.env.VITE_COGNITO_CLIENT_ID!,
    })
    return instance
  }

  throw new Error(`[Auth] Unknown provider: "${provider}"`)
}
